import {driverCapabilities,DRIVER_TRIP_UNAVAILABLE,applyDriverCapabilities} from '../../api/driverPartner';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { fetchWallet, walletInsufficient } from './walletSlice';
import { WALLET_REQUIRED_MESSAGE } from '../../utils/wallet';
import axiosInstance from '../../config/axiosConfig';
import {
  getErrorMessage,
  assertApiSuccess,
  extractPayload,
  getApiErrorCode,
  normalizeDriverOrder,
  normalizeDriverOrdersResponse,
} from '../../utils/apiResponseUtils';
import {
  normalizeDriverDocumentsResponse,
} from '../../utils/driverDocuments';

const ACTIVE_DRIVER_ORDER_STATUSES = ['ASSIGNED', 'PICKED_UP', 'IN_TRANSIT', 'OUT_FOR_DELIVERY'];

const DRIVER_ORDERS_ENDPOINTS = [
  '/driver/orders',
];

let resolvedDriverOrdersEndpoint = null;

const shouldTryNextEndpoint = (error) => (
  !error?.response ||
  [404, 405].includes(error.response.status)
);

const requestFirstAvailable = async (requestFactory, endpoints) => {
  let lastError = null;
  const prioritizedEndpoints = resolvedDriverOrdersEndpoint
    ? [resolvedDriverOrdersEndpoint, ...endpoints.filter((endpoint) => endpoint !== resolvedDriverOrdersEndpoint)]
    : endpoints;

  for (const endpoint of prioritizedEndpoints) {
    try {
      const response = await requestFactory(endpoint);
      resolvedDriverOrdersEndpoint = endpoint;
      return response;
    } catch (error) {
      lastError = error;

      if (!shouldTryNextEndpoint(error)) {
        throw error;
      }
    }
  }

  throw lastError;
};

const getDriverOrdersErrorMessage = (error) => {
  if (['PHONE_NOT_VERIFIED', 'EMAIL_NOT_VERIFIED'].includes(getApiErrorCode(error))) return getErrorMessage(error);
  const status = error?.response?.status;
  const responseData = error?.response?.data || {};
  const approvalStatus = responseData.approvalStatus || responseData.approval_status;

  if (!status) {
    return `Could not load your assignments. Reconnect and refresh.`;
  }

  if (status === 401) {
    return 'Driver session is invalid or expired. Please log in again as a driver.';
  }

  if (status === 403) {
    if (approvalStatus) {
      return `Driver approval is ${String(approvalStatus).toLowerCase()}. Orders unlock after approval.`;
    }

    return 'This account does not have active driver access.';
  }

  if (status === 404) {
    return 'Assignments are currently unavailable. Try again later.';
  }

  if (status >= 500) {
    return 'Assignments could not be refreshed. Try again later.';
  }

  return getErrorMessage(error, 'Failed to fetch orders');
};

const getDriverOrderActionErrorMessage = (error, actionLabel, fallbackMessage) => {
  if (['PHONE_NOT_VERIFIED', 'EMAIL_NOT_VERIFIED'].includes(getApiErrorCode(error))) return getErrorMessage(error);
  const status = error?.response?.status;
  const requestUrl = error?.config?.url || DRIVER_ORDERS_ENDPOINTS[0];
  const responseMessage = getErrorMessage(error, fallbackMessage, {
    ignoreMappedCodes: ['UNAUTHORIZED', 'FORBIDDEN'],
  });

  if (!status) {
    return `Could not reach ${actionLabel} endpoint at ${requestUrl}. Check backend/CORS and try again.`;
  }

  if (status === 401) {
    return `Driver session is invalid while trying to ${actionLabel}. Please log in again as a driver.`;
  }

  if (status === 403) {
    return `Driver is not allowed to ${actionLabel}. Confirm driver role, active status, and approval.`;
  }

  if (status === 404) {
    return `${actionLabel} endpoint was not found at ${requestUrl}. Confirm the backend path.`;
  }

  if (status === 409) {
    return responseMessage || `Order can no longer be ${actionLabel}ed. Refreshing orders may show the latest status.`;
  }

  return `${responseMessage || fallbackMessage} (HTTP ${status})`;
};

const confirmedOrder=(response,id,statuses)=>{
 assertApiSuccess(response);const order=extractPayload(response.data);
 if(!order||String(order.id)!==String(id)||!statuses.includes(order.status))throw new Error('Trip state is not confirmed. Refresh the assignment before retrying.');
 return normalizeDriverOrder(order);
};

const getDriverOrderEndpoint = (orderId, suffix = '') => {
  const baseEndpoint = resolvedDriverOrdersEndpoint || DRIVER_ORDERS_ENDPOINTS[0];
  return `${baseEndpoint}/${encodeURIComponent(orderId)}${suffix}`;
};

async function recoverableTripAction(id, suffix, body, statuses, config={}) {
 try { const response = await axiosInstance.post(getDriverOrderEndpoint(id,suffix),body,config); confirmedOrder(response,id,statuses); const fresh=await readOwnedTrip(id);return {...fresh,data:{success:true,data:{...extractPayload(response.data),...extractPayload(fresh.data)}}}; }
 catch(error) { if(!error?.response || error.response.status>=500) { const response=await readOwnedTrip(id); confirmedOrder(response,id,statuses); return response; } throw error; }
}
const mapTrip=trip=>{
 const payment=trip.payment||trip.paymentAllocation;
 return payment?.allocationStatus==='KNOWN'?{...trip,parcelWeight:trip.parcelWeight??trip.parcelWeightKg,estimatedCost:trip.estimatedCost??payment.farePaise/100,finalFare:payment.farePaise,paymentStatus:payment.paymentStatus,cashDue:payment.cashRemainingPaise,cashPaid:payment.cashVerifiedPaise,walletPaid:payment.walletPaidPaise,onlinePaid:payment.onlinePaidPaise,cashAcknowledged:trip.cashAcknowledgments?.some(item=>item.status==='ACKNOWLEDGED')}:trip;
};
async function readOwnedTrip(id){const response=await axiosInstance.get('/driver/trips/'+encodeURIComponent(id));assertApiSuccess(response);return {...response,data:{success:true,data:mapTrip(extractPayload(response.data))}};}
const upsertCompletedOrder = (orders, order) => {
  if (!order) {
    return orders;
  }

  const orderId = order.id || order.orderId;
  const existingIndex = orders.findIndex(
    (completedOrder) => (completedOrder.id || completedOrder.orderId) === orderId
  );

  if (existingIndex >= 0) {
    orders[existingIndex] = order;
  } else {
    orders.unshift(order);
  }

  return orders;
};

// Fetch assigned orders for the driver
export const fetchDriverOrders = createAsyncThunk(
  'driver/fetchDriverOrders',
  async (_, { rejectWithValue, getState }) => {
    const accountId=getState().auth?.user?.id;
    try {
      const response = await requestFirstAvailable(
        (endpoint) => axiosInstance.get(endpoint),
        DRIVER_ORDERS_ENDPOINTS
      );
      assertApiSuccess(response);
      if(getState().auth?.user?.id!==accountId)return [];
      const capabilities=await axiosInstance.get('/driver/capabilities');assertApiSuccess(capabilities);applyDriverCapabilities(capabilities.data);
      const orders=await Promise.all(normalizeDriverOrdersResponse(response.data).map(async order=>order.status==='PENDING'?normalizeDriverOrder(mapTrip(order)):normalizeDriverOrder({...order,...extractPayload((await readOwnedTrip(order.id)).data)})));
      const currentResponse=await axiosInstance.get('/driver/trips/current');
      assertApiSuccess(currentResponse);
      const assigned=extractPayload(currentResponse.data)?.trip;
      if(assigned){
        const confirmed=normalizeDriverOrder(mapTrip(assigned));
        const index=orders.findIndex(order=>String(order.id)===String(confirmed.id));
        if(index>=0)orders[index]=confirmed;else orders.unshift(confirmed);
      }
      const current=getState().driver.activeOrder;
      if(current?.status==='DELIVERED'&&!orders.some(order=>order.id===current.id))orders.push(normalizeDriverOrder({...current,...extractPayload((await readOwnedTrip(current.id)).data)}));
      return orders;
    } catch (error) {
      return rejectWithValue(getDriverOrdersErrorMessage(error));
    }
  }, {condition: (_, {getState})=>!getState().driver.ordersRefreshing}
);

// Accept an order
export const acceptOrder = createAsyncThunk(
  'driver/acceptOrder',
  async (orderId, { rejectWithValue, dispatch }) => {
    if(!driverCapabilities.accept)return rejectWithValue(DRIVER_TRIP_UNAVAILABLE);
    if (!orderId) {
      return rejectWithValue('Unable to accept this order because the order id is missing.');
    }

    try {
      const wallet = await dispatch(fetchWallet({force:true})).unwrap();
      if (wallet.canAcceptOrders !== true) return rejectWithValue(WALLET_REQUIRED_MESSAGE);
      const response = await recoverableTripAction(orderId, '/accept', {}, ['ASSIGNED','PICKED_UP','IN_TRANSIT']);
      assertApiSuccess(response);
      const normalizedOrder = confirmedOrder(response,orderId,['ASSIGNED','PICKED_UP','IN_TRANSIT']);
      
      return normalizedOrder;
    } catch (error) {
      if (getApiErrorCode(error) === 'INSUFFICIENT_DRIVER_WALLET_BALANCE') { dispatch(walletInsufficient()); return rejectWithValue(WALLET_REQUIRED_MESSAGE); }
      if (typeof error === 'string') return rejectWithValue(error);
      return rejectWithValue(getDriverOrderActionErrorMessage(error, 'accept order', 'Failed to accept order'));
    }
  }
);

export const cancelOrder = createAsyncThunk(
  'driver/cancelOrder',
  async (orderId, { rejectWithValue, getState }) => {
    const order=getState().driver.orders.find(item=>String(item.id||item.orderId)===String(orderId)) || getState().driver.activeOrder;
    if(!order || String(order.id||order.orderId)!==String(orderId) || !['PENDING','ASSIGNED'].includes(order.status))return rejectWithValue('Drivers can only cancel before sender OTP verification.');
    if(!driverCapabilities.cancellation)return rejectWithValue(DRIVER_TRIP_UNAVAILABLE);
    try {
      const response = await axiosInstance.post(getDriverOrderEndpoint(orderId, '/cancel'));
      return {
        orderId,
        message: response.data?.message || 'Order declined. It remains available to other drivers.',
        order: confirmedOrder(response,orderId,['PENDING']),
      };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to cancel order'));
    }
  }
);

// Verify pickup OTP
export const verifyPickupOtp = createAsyncThunk(
  'driver/verifyPickupOtp',
  async ({ orderId, otp }, { rejectWithValue, dispatch }) => {
    if(!driverCapabilities.pickup)return rejectWithValue(DRIVER_TRIP_UNAVAILABLE);
    try {
      const response = await recoverableTripAction(orderId, '/verify-pickup', { otp }, ['PICKED_UP','IN_TRANSIT','DELIVERED']);
      return {
        message: response.data?.message || 'Pickup verified successfully.',
        order: normalizeDriverOrder(response.data),
      };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'OTP verification failed'));
    }
  }
);

// Verify delivery OTP and complete delivery
export const verifyDeliveryOtp = createAsyncThunk(
  'driver/verifyDeliveryOtp',
  async ({ orderId, otp }, { rejectWithValue, dispatch }) => {
    if(!driverCapabilities.complete)return rejectWithValue(DRIVER_TRIP_UNAVAILABLE);
    try {
      const response = await recoverableTripAction(orderId, '/verify-delivery', { otp }, ['DELIVERED']);
      assertApiSuccess(response);
      dispatch(fetchWallet());
      return {
        message: response.data?.message || 'Delivery completed successfully.',
        order: normalizeDriverOrder(response.data),
      };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Delivery OTP verification failed'));
    }
  }
);

// Get masked customer phone number
export const acknowledgeCash = createAsyncThunk('driver/acknowledgeCash', async ({orderId,amount}, {rejectWithValue,getState}) => {
 if(!driverCapabilities.cashCollection)return rejectWithValue('Cash receipt is unavailable on this backend.');
 try {
  if(!Number.isSafeInteger(amount)||amount<=0)throw new Error('Cash amount is not confirmed.');
  const trip=extractPayload((await readOwnedTrip(orderId)).data);
  if(!trip.cashAcknowledgments?.some(item=>['ACKNOWLEDGED','VERIFIED'].includes(item.status))){
   try{const response=await axiosInstance.post('/driver/trips/'+encodeURIComponent(orderId)+'/cash-acknowledgments',{version:trip.version,amountPaise:amount,method:'CASH'},{headers:{'Idempotency-Key':'cash-ack-'+orderId+'-'+trip.version}});assertApiSuccess(response);}catch(error){if(error?.response&&error.response.status<500)throw error;const recovered=confirmedOrder(await readOwnedTrip(orderId),orderId,['DELIVERED']);if(recovered.cashAcknowledged||recovered.cashDue===0)return recovered;throw error;}
  }
  const confirmed=confirmedOrder(await readOwnedTrip(orderId),orderId,['DELIVERED']);
  if(confirmed.cashDue>0&&!confirmed.cashAcknowledged)throw new Error('Cash acknowledgment is not confirmed. Refresh the existing assignment.');
  return {...getState().driver.activeOrder,...confirmed};
 }catch(error){return rejectWithValue(getErrorMessage(error,error.message||'Cash receipt could not be confirmed.'));}
});
export const getMaskedNumber = createAsyncThunk(
  'driver/getMaskedNumber',
  async (orderId, { rejectWithValue }) => {
    if(!driverCapabilities.maskedContact)return rejectWithValue(DRIVER_TRIP_UNAVAILABLE);
    try {
      const response = await axiosInstance.get(getDriverOrderEndpoint(orderId, '/masked-number'));
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to get contact number'));
    }
  }
);

// Update driver location
export const updateDriverLocation = createAsyncThunk(
  'driver/updateDriverLocation',
  async ({ lat, lng }, { rejectWithValue }) => {
    if(!driverCapabilities.location)return rejectWithValue(DRIVER_TRIP_UNAVAILABLE);
    try {
      const response = await axiosInstance.post('/driver/location', { lat, lng });
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to update location'));
    }
  }
);

export const fetchDriverDocuments = createAsyncThunk(
  'driver/fetchDriverDocuments',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('/driver/documents');
      assertApiSuccess(response);
      return normalizeDriverDocumentsResponse(response.data);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to load driver documents'));
    }
  },{condition:(_,{getState})=>!getState().driver.documentsLoading}
);

export const uploadDriverDocument = createAsyncThunk(
  'driver/uploadDriverDocument',
  async (files, { rejectWithValue, getState }) => {
    try {
      const keys=['aadhar','dl','rc'];
      const selected=keys.filter(key=>files[key] instanceof File);
      const existing=getState().driver.documents;
      if(!selected.length||!keys.every(key=>files[key] instanceof File||existing?.[key]?.name))throw new Error('Select all three PDF files before submitting.');
      if(selected.length<3){
        for(const key of selected){
          const form=new FormData();form.append('documentType',key.toUpperCase());form.append('file',files[key]);
          assertApiSuccess(await axiosInstance.post('/driver/documents',form));
        }
        const refreshed=await axiosInstance.get('/driver/documents');assertApiSuccess(refreshed);
        return normalizeDriverDocumentsResponse(refreshed.data);
      }
      const formData = new FormData();
      for (const key of ['aadhar', 'dl', 'rc']) {
        if (!(files[key] instanceof File)) throw new Error('Select all three PDF files before submitting.');
        formData.append(key, files[key]);
      }
      const response = await axiosInstance.post('/driver/documents/submit', formData);
      assertApiSuccess(response);
      return normalizeDriverDocumentsResponse(response.data);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, error.message || 'Failed to submit driver documents'));
    }
  }
);

const initialState = {
  orders: [],
  completedOrders: [],
  ordersRefreshing: false,
  ordersRequestId: null,
  activeOrder: null,
  maskedNumber: null,
  loading: false,
  error: null,
  otpVerified: false,
  locationUpdating: false,
  documents: {},
  documentReview: null,
  documentsLoaded: false,
  documentsLoading: false,
  documentsSubmitting: false,
};

const driverSlice = createSlice({
  name: 'driver',
  initialState,
  reducers: {
    clearDriverError: (state) => {
      state.error = null;
    },
    clearMaskedNumber: (state) => {
      state.maskedNumber = null;
    },
    setActiveOrder: (state, action) => {
      state.activeOrder = action.payload;
      state.otpVerified = ['PICKED_UP', 'IN_TRANSIT', 'DELIVERED'].includes(action.payload?.status);
      state.maskedNumber = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(acknowledgeCash.pending,state=>{state.loading=true;state.error=null;})
      .addCase(acknowledgeCash.fulfilled,(state,action)=>{state.loading=false;state.activeOrder=action.payload;upsertCompletedOrder(state.completedOrders,action.payload);})
      .addCase(acknowledgeCash.rejected,(state,action)=>{state.loading=false;state.error=action.payload;})
      .addCase('auth/logout', () => ({ ...initialState, completedOrders: [] }))
      .addCase('auth/loginUser/rejected', (state) => { if (!sessionStorage.getItem('authToken')) return { ...initialState, completedOrders: [] }; })
      .addCase('auth/socialLogin/rejected', (state) => { if (!sessionStorage.getItem('authToken')) return { ...initialState, completedOrders: [] }; })
      .addCase('auth/verifyMobileOtp/rejected', (state) => { if (!sessionStorage.getItem('authToken')) return { ...initialState, completedOrders: [] }; })
      .addCase('auth/logoutUser/fulfilled', () => ({ ...initialState, completedOrders: [] }))
      .addCase('auth/refreshCurrentUser/rejected', () => ({ ...initialState, completedOrders: [] }))
      // Fetch orders
      .addCase(fetchDriverOrders.pending, (state, action) => {
        state.ordersRequestId = action.meta.requestId;
        state.ordersRefreshing = true;
        if (state.orders.length === 0) {
          state.loading = true;
        }
        state.error = null;
      })
      .addCase(fetchDriverOrders.fulfilled, (state, action) => {
        if(state.ordersRequestId !== action.meta.requestId)return;
        state.ordersRequestId = null;
        state.ordersRefreshing = false;
        state.loading = false;
        const fetchedOrders = Array.isArray(action.payload) ? action.payload : [];
        state.orders = fetchedOrders;

        const fetchedCompletedOrders = fetchedOrders.filter((order) => order.status === 'DELIVERED');
        fetchedCompletedOrders.forEach((order) => {
          upsertCompletedOrder(state.completedOrders, order);
        });

        const fetchedActiveOrder = state.orders.find((order) => (
          (ACTIVE_DRIVER_ORDER_STATUSES.includes(order.status)||(order.status==='DELIVERED'&&Number(order.cashDue)>0))
        ));

        if (fetchedActiveOrder) {
          state.activeOrder = fetchedActiveOrder;
          state.otpVerified = ['PICKED_UP', 'IN_TRANSIT'].includes(fetchedActiveOrder.status);
        } else if (state.activeOrder) {
          const refreshedActiveOrder = state.orders.find(
            (order) => order.id === state.activeOrder.id || order.id === state.activeOrder.orderId
          );

          if (refreshedActiveOrder && [...ACTIVE_DRIVER_ORDER_STATUSES,'DELIVERED'].includes(refreshedActiveOrder.status)) {
            state.activeOrder = refreshedActiveOrder;
          } else {
            state.activeOrder = null;
            state.otpVerified = false;
            state.maskedNumber = null;
          }
        }
      })
      .addCase(fetchDriverOrders.rejected, (state, action) => {
        if(state.ordersRequestId !== action.meta.requestId)return;
        state.ordersRequestId = null;
        state.ordersRefreshing = false;
        state.loading = false;
        state.error = state.orders.length === 0 ? action.payload : null;
      })
      // Accept order
      .addCase(acceptOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(acceptOrder.fulfilled, (state, action) => {
        // A refresh begun before acceptance must not erase the confirmed assignment.
        state.ordersRequestId = null;
        state.ordersRefreshing = false;
        state.loading = false;
        const idx = state.orders.findIndex((o) => o.id === action.payload.id);
        const existingOrder = idx >= 0 ? state.orders[idx] : state.activeOrder;
        const mergedOrder = existingOrder
          ? normalizeDriverOrder({ ...existingOrder, ...action.payload })
          : action.payload;

        state.activeOrder = mergedOrder;

        if (idx >= 0) {
          state.orders[idx] = mergedOrder;
        }
      })
      .addCase(acceptOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(cancelOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cancelOrder.fulfilled, (state, action) => {
        state.loading = false;
        const cancelledOrderId = action.payload.orderId;
        state.orders = state.orders.filter((order) => (
          (order.id || order.orderId) !== cancelledOrderId
        ));
        if (
          state.activeOrder &&
          (state.activeOrder.id || state.activeOrder.orderId) === cancelledOrderId
        ) {
          state.activeOrder = null;
          state.otpVerified = false;
          state.maskedNumber = null;
        }
      })
      .addCase(cancelOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Verify OTP
      .addCase(verifyPickupOtp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyPickupOtp.fulfilled, (state, action) => {
        state.loading = false;
        state.otpVerified = true;
        if (state.activeOrder) {
          state.activeOrder = normalizeDriverOrder({
            ...state.activeOrder,
            ...(action.payload.order || {}),
            status: action.payload.order?.status,
          });
          const idx = state.orders.findIndex((order) => order.id === state.activeOrder.id);
          if (idx >= 0) {
            state.orders[idx] = state.activeOrder;
          }
        }
      })
      .addCase(verifyPickupOtp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.otpVerified = false;
      })
      // Verify delivery OTP and complete delivery
      .addCase(verifyDeliveryOtp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyDeliveryOtp.fulfilled, (state, action) => {
        state.loading = false;
        const deliveredOrder = state.activeOrder
          ? normalizeDriverOrder({
              ...state.activeOrder,
              ...(action.payload.order || {}),
              status: action.payload.order?.status,
            })
          : action.payload.order;
        state.orders = state.orders.filter((o) => o.id !== deliveredOrder?.id);
        if (deliveredOrder) {
          upsertCompletedOrder(state.completedOrders, deliveredOrder);
        }
        state.activeOrder = deliveredOrder;
        state.otpVerified = false;
        state.maskedNumber = null;
      })
      .addCase(verifyDeliveryOtp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Get masked number
      .addCase(getMaskedNumber.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getMaskedNumber.fulfilled, (state, action) => {
        state.loading = false;
        state.maskedNumber = action.payload?.maskedNumber || action.payload?.number || action.payload;
      })
      .addCase(getMaskedNumber.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update location
      .addCase(updateDriverLocation.pending, (state) => {
        state.locationUpdating = true;
      })
      .addCase(updateDriverLocation.fulfilled, (state) => {
        state.locationUpdating = false;
      })
      .addCase(updateDriverLocation.rejected, (state) => {
        state.locationUpdating = false;
      })
      .addCase(fetchDriverDocuments.pending, (state) => {
        state.documentsLoading = true;
      })
      .addCase(fetchDriverDocuments.fulfilled, (state, action) => {
        state.documentsLoading = false;
        state.documentsLoaded = true;
        state.documents = action.payload.documents || {};
        state.documentReview = {
          ...action.payload,
          status: action.payload.approvalStatus,
          reason: action.payload.reason || '',
        };
      })
      .addCase(fetchDriverDocuments.rejected, (state, action) => {
        state.documentsLoading = false;
        state.documentsLoaded = false; state.documentReview = null;
        state.error = action.payload;
      })
      .addCase(uploadDriverDocument.pending, (state) => {
        state.documentsSubmitting = true;
        state.error = null;
      })
      .addCase(uploadDriverDocument.fulfilled, (state, action) => {
        state.documentsSubmitting = false;
        state.documents = {
          ...state.documents,
          ...(action.payload.documents || {}),
        };
        state.documentReview = {
          ...action.payload,
          status: action.payload.approvalStatus,
          reason: action.payload.reason || '',
        };
      })
      .addCase(uploadDriverDocument.rejected, (state, action) => {
        state.documentsSubmitting = false;
        state.error = action.payload;
      });
  },
});

export const { clearDriverError, clearMaskedNumber, setActiveOrder } = driverSlice.actions;
const driverReducer=driverSlice.reducer;
export default function reducer(state,action){
 const match=/^(driver\/.+)\/(pending|fulfilled|rejected)$/.exec(action.type||'');
 if(match&&action.meta?.requestId){
  const [ ,operation,phase]=match;
  if(phase!=='pending'&&state?.requestIds?.[operation]!==action.meta.requestId)return state;
  const next=driverReducer(state,action),requestIds={...(next.requestIds||{})};
  if(phase==='pending')requestIds[operation]=action.meta.requestId;else delete requestIds[operation];
  return {...next,requestIds};
 }
 return driverReducer(state,action);
}
